/**
 * Lightweight event bus for "something changed, please refresh stats".
 *
 *   import { emitStatsChanged, useStatsBus } from "@/lib/statsBus";
 *
 *   // 1) Emit after any successful mutation:
 *   emitStatsChanged("match:validated", { matchId });
 *
 *   // 2) Subscribe in stats-showing components:
 *   useStatsBus(() => { load(); });
 *
 * The bus is intentionally simple (no external deps). It uses window
 * CustomEvents so it works across React trees and portals.
 */

const EVENT_NAME = "dyd:stats-changed";

/**
 * Emit a stats-changed event. Optional `reason` and `payload` are attached to
 * event.detail so listeners can filter if they want.
 */
export function emitStatsChanged(reason = "unknown", payload = {}) {
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { reason, payload, ts: Date.now() } }));
  } catch (_) {
    /* no-op in SSR / test env */
  }
}

// React hook — supplied at module bottom to keep this file framework-agnostic-ish.
import { useEffect, useRef } from "react";

/**
 * Subscribe to stats-changed events. Automatically debounced to avoid a burst
 * of API calls when several mutations happen in quick succession.
 */
export function useStatsBus(callback, { debounceMs = 250 } = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    let timer = null;
    const handler = (e) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          cbRef.current?.(e.detail || {});
        } catch (_) { /* swallow */ }
      }, debounceMs);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      clearTimeout(timer);
    };
  }, [debounceMs]);
}
